import { observable, computed, action, reaction } from 'mobx'
import Auth, { CognitoUser } from '@aws-amplify/auth'
import { Hub, ICredentials } from '@aws-amplify/core'
import { UserData, CognitoUserAttribute } from 'amazon-cognito-identity-js'
import DynamoDB from 'aws-sdk/clients/dynamodb'

import { objectToUpdateItemAttributes } from '~/utils'
import { cognitoConfig } from '~/config'
import { TUserAttributes, TUserProfile } from '~/types'
import debounce from 'lodash.debounce'
import { AnalyticsStore } from './analytics'

export class UserStore {
  private db: DynamoDB

  @observable
  public loading: boolean

  /** UserStore.user represents cognito user  */
  @observable
  public user?: CognitoUser = undefined

  /** UserStore.userData represents cognito user data  */
  @observable
  public userData?: UserData = undefined

  @observable
  public userAttributes?: CognitoUserAttribute[] = undefined

  /** UserStore.userCredentials represents cognito user credentials  */
  @observable
  public userCredentials?: ICredentials = undefined

  /** UserStore.userCredentials represents custom user profile info  */
  @observable
  public userProfile?: TUserProfile = {
    contacts: {},
  }

  /**
   * AuthStore.constructor is used to create new AuthStore instance.
   * Fetches current user state and subscribes to auth events.
   */
  constructor() {
    Auth.configure(cognitoConfig)

    Hub.listen('auth', (event) => {
      if (event.payload.event === 'signIn') {
        this.getUserInfo()
      } else if (event.payload.event === 'signOut') {
        this.user = null
        this.userData = null
        this.userAttributes = null
        this.userCredentials = null
      } else {
        console.debug('unknown event', event)
      }
    })

    // if auth redirect, wait until cognito picks up authenticated user
    if (window.location.pathname !== '/sign-in/success') {
      this.getUserInfo()
    }

    // listen to user changes
    reaction(() => this.userID, AnalyticsStore.setUserID)
  }

  /** UserStore.ready returns true if store has loaded all data and is ready for use */
  @computed
  get ready() {
    return Boolean(
      this.user !== undefined &&
        this.userData !== undefined &&
        this.userAttributes !== undefined &&
        this.userCredentials !== undefined,
    )
  }

  /** UserStore.authenticated returns true if user is logged in */
  @computed
  public get authenticated() {
    return Boolean(this.user && this.userData)
  }

  /** UserStore.userID is an alias to UserStore.userAttributesObject.sub */
  @computed
  get userID() {
    return this.userAttributesObject?.sub
  }

  /** UserStore.userAttributes returns user attributes in a key-value object */
  @computed
  get userAttributesObject(): TUserAttributes {
    return (
      this.userData &&
      this.userData.UserAttributes.reduce(
        (acc, cur) => ({
          ...acc,
          [cur['Name']]: cur['Value'],
        }),
        {} as TUserAttributes,
      )
    )
  }

  /**
   * UserStore.getUserInfo restores signed user session
   * and fetches user data, attributes and credentials.
   */
  @action
  private getUserInfo = () =>
    Auth.currentUserPoolUser()
      .then((user: CognitoUser) => {
        this.user = user

        user.setSignInUserSession(user.getSignInUserSession())

        user.getUserData((err, data) => {
          if (err) console.error('failed to get user data', err)
          else this.userData = data
        })

        user.getUserAttributes((err, attributes) => {
          if (err) console.error('failed to get user attributes', err)
          else this.userAttributes = attributes
        })

        Auth.currentCredentials()
          .then((credentials) => {
            this.userCredentials = credentials

            this.db = new DynamoDB({
              region: 'us-east-1',
              credentials: this.userCredentials,
            })
          })
          .catch((err) => console.log('failed to get user credentials', err))
      })
      .catch((err) => {
        console.debug(err)

        this.user = null
        this.userData = null
        this.userAttributes = null
        this.userCredentials = null
      })

  /** UserStore.getUserProfile is used to get user profile */
  @action
  public getUserProfile = () =>
    this.db
      .getItem({
        TableName: 'users-preprod.part-scout.com',
        Key: { ID: { S: this.userID } },
      })
      .promise()
      .then((t) => DynamoDB.Converter.unmarshall(t.Item) as TUserProfile)
      .then((profile) => {
        this.userProfile = profile || this.userProfile
      })

  /** UserStore.getUserProfile is used to update user profile */
  @action
  public setUserProfile = (profile: TUserProfile) => {
    this.userProfile = {
      ...this.userProfile,
      ...profile,
    }

    this.loading = true

    return this.saveUserProfile()
  }

  /** UserStore.getUserProfile is used to save user profile to db with debouncing */
  @action
  public saveUserProfile = debounce(() => {
    this.loading = true

    return this.db
      .updateItem({
        TableName: 'users-preprod.part-scout.com',
        Key: { ID: { S: this.userID } },
        ...objectToUpdateItemAttributes({
          ...this.userProfile,
          ID: null,
        }),
      })
      .promise()
      .catch(console.error)
      .finally(() => {
        this.loading = false
      })
  }, 400)

  /** UserStore.signOut is used to sign currently signed user out */
  @action
  public signOut = () => {
    localStorage.clear()

    return Auth.signOut()
  }
}
